import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { useTheme } from '../ThemeContext.tsx';

type InfoTooltipProps = {
    text: string;
};

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text }) => {
    const { colors } = useTheme();
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
    const iconRef = React.useRef<HTMLDivElement>(null);

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            setTooltipPos({
                top: rect.top - 8,
                left: rect.right + 8
            });
        }
        setIsVisible(true);
    };

    const handleMouseLeave = () => {
        setIsVisible(false);
    };

    // Close on outside click
    useEffect(() => {
        if (!isVisible) return;
        const handle = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('#info-tooltip')) return;
            setIsVisible(false);
        };
        window.addEventListener('click', handle);
        return () => window.removeEventListener('click', handle);
    }, [isVisible]);

    return (
        <>
            <div
                ref={iconRef}
                className="cursor-pointer"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Info size={14} className="text-blue-500" />
            </div>

            {/* Fixed-position tooltip rendered when visible */}
            {isVisible && tooltipPos && (
                <div
                    id="info-tooltip"
                    className={`fixed z-50 ${colors.bg.primary} border ${colors.border.primary} rounded-md shadow-xl p-3 text-sm text-blue-600 max-w-64`}
                    style={{ top: tooltipPos.top, left: tooltipPos.left }}
                >
                    {text}
                </div>
            )}
        </>
    );
};

export default InfoTooltip;
